<template>
  <template v-for="(part, index) in expressionList" :key="part.uuid">
    <expression-part
      :part="part"
      :edit="edit[index]"
      :suggestions="suggestionsFor(part.uuid)"
      @cursor-leave-left="cursorLeaveLeft"
      @cursor-leave-right="cursorLeaveRight"
      @fetch-tools="(uuid: string) => emit('fetchTools', uuid)"
      @selected-tool="
        (tool: Tool, uuid: string) => {
          emit('selectedTool', tool, uuid)
          searchText = ''
        }
      "
    ></expression-part>
  </template>
  <v-combobox
    v-model:search="searchText"
    style="display: inline-block"
    min-width="10em"
    :items="filteredSuggestions"
    :focused="focusAdditionalInput"
    item-title="label"
    auto-select-first
    variant="plain"
    single-line
    hide-details
    hide-spin-buttons
    hide-no-data
    hide-selected
    @update:focused="focuseChanged"
    @update:menu="focuseChanged"
    @update:model-value="
      (item) => {
        if (item && props.expression.uuid && typeof item != 'string')
          emit('selectedTool', item, props.expression.uuid)
      }
    "
  ></v-combobox>
</template>

<script setup lang="ts">
  import { Filter, Tool } from '@/models/custom-metric.model'
  import { computed, ComputedRef, ref } from 'vue'
  import ExpressionPart from './ExpressionPart.vue'

  interface Props {
    expression: Filter
    suggestions: Tool[]
  }
  const props = defineProps<Props>()

  const emit = defineEmits<{
    selectedTool: [tool: Tool, location: string]
    fetchTools: [uuid: string]
  }>()
  const searchText = defineModel<string>()

  const expressionList: ComputedRef<Filter[]> = computed(() => getExpressionList(props.expression))
  const edit = ref<boolean[]>(expressionList.value.map(() => false))

  const focusAdditionalInput = ref<boolean>(false)
  const filteredSuggestions = computed(() =>
    props.suggestions.filter(
      (s) =>
        s.type == 'expression' &&
        (!s.targets ||
          s.targets.length == 0 ||
          (props.expression.uuid && s.targets.indexOf(props.expression.uuid) != -1)),
    ),
  )

  function getExpressionList(exp: Filter): Filter[] {
    switch (exp.type) {
      case 'eq':
      case 'ne':
      case 'contains':
      case 'and':
      case 'toolbox.assist.binary':
        return [...getExpressionList(exp.left), exp, ...getExpressionList(exp.right)]
      case 'literal':
      case 'field':
      case 'toolbox.assist':
        return [exp]
    }
  }

  function focuseChanged(focus: boolean) {
    if (focus && props.expression.uuid) {
      emit('fetchTools', props.expression.uuid)
    }
  }

  function suggestionsFor(uuid?: string): Tool[] {
    if (!uuid) {
      return []
    }
    return props.suggestions.filter((tool) => tool.targets && tool.targets.indexOf(uuid) != -1)
  }

  function cursorLeaveLeft(uuid: string) {
    const index = expressionList.value.findIndex((e) => e.uuid == uuid)
    if (index != -1) {
      edit.value[index] = false
      if (index > 0) {
        edit.value[index - 1] = true
      }
    }
  }
  function cursorLeaveRight(uuid: string) {
    const index = expressionList.value.findIndex((e) => e.uuid == uuid)
    if (index != -1) {
      edit.value[index] = false
      if (index + 1 < expressionList.value.length) {
        edit.value[index + 1] = true
      } else {
        focusAdditionalInput.value = true
      }
    }
  }
</script>
